"use client"

import {
  ArrowRight,
  Bread,
  ChartLineUp,
  Drop,
  Fish,
  ForkKnife,
  Heartbeat,
  Hourglass,
  House,
  Leaf,
  MagnifyingGlass,
  Pill,
  ShieldCheck,
  Stethoscope,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react"
import Image from "next/image"
import Link from "next/link"
import { blobMediaUrl } from "@/lib/blob-url"
import { useTranslations } from "@/lib/intl/use-translations"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { cn } from "@/lib/utils"
import { PartnerLogoMarquee } from "@/components/ui/partner-logo-marquee"
import { HomeV4Close } from "../home/home-v4-close"
import { HomeV4Compare } from "../home/home-v4-compare"
import { BookButton, usePlanPrices } from "./pricing-v5-shared"

// Match the current MRI overview's calm backdrop and typography, with
// outcomes specific to the baseline blood panel (no add-on biomarkers).
const BACKGROUND = blobMediaUrl(
  "/images/marketing/mri-finds/mri-finds-bg-20261002-v5.webp"
)
// Energy first, heart third: most people come for how they feel, not for
// heart risk (Michèle, AHD-13799, 09.10).
const BLOOD_ICONS = [Leaf, ChartLineUp, Heartbeat, Drop] as const
// Fine grain over the landing's background photo, as on the quiz banner.
const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='grain'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' seed='7' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Cpath fill='%23808080' filter='url(%23grain)' d='M0 0h256v256H0z'/%3E%3C/svg%3E")`
// The icon is cut away under its check badge, so the badge needs no ring.
// Badge 24px, centred 38px into the 48px icon; the cut is 2px wider.
const BADGE_CUT =
  "radial-gradient(circle at 38px 38px, transparent 14px, #000 14.5px)"

type FindsPrefix =
  | "bloodFinds"
  | "coeliacFinds"
  | "heavyMetalsFinds"
  | "traceElementsFinds"

/**
 * Why to take a blood test, as a heading and four cards under the hero.
 * `prefix` picks the copy (`{prefix}Heading`, `{prefix}{1-4}Lead|Rest` in
 * `pricingV5`): the advanced blood test by default, and the add-on tests'
 * own pages with theirs, where the last card says what the test does not do
 * (Sven, AHD-13640, 06.10).
 */
export function BloodFindsCards({
  prefix = "bloodFinds",
  icons = BLOOD_ICONS,
  offer,
}: {
  prefix?: FindsPrefix
  icons?: readonly PhosphorIcon[]
  /** The `/go/bloodtest` landing: frosted cards and a check on each icon.
   *  Its offer follows as `BloodOfferSection` (Eugene, 07.10). */
  offer?: boolean
}) {
  const t = useTranslations("pricingV5")
  const finds = icons.map((icon, i) => ({ icon, n: (i + 1) as 1 | 2 | 3 | 4 }))

  return (
    <section
      id="blood-finds"
      aria-labelledby="blood-finds-heading"
      className="relative isolate overflow-hidden pt-14 pb-16 md:pt-[84px] md:pb-24"
    >
      <Image
        src={BACKGROUND}
        alt=""
        fill
        sizes="100vw"
        quality={90}
        // The landing zooms in from the top so the photo's pale bottom edge
        // stays out of frame, and lays a light grain over it (Eugene, 07.10).
        className={cn("-z-10 object-cover", offer && "origin-top scale-[1.15]")}
      />
      {offer && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-30 mix-blend-soft-light"
          style={{ backgroundImage: GRAIN }}
        />
      )}
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <h2
          id="blood-finds-heading"
          className="type-display-sm-emphasized mx-auto max-w-[734px] text-center text-balance text-neutral-900"
        >
          {t(`${prefix}Heading`)}
        </h2>
        <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-[78px] lg:grid-cols-4">
          {finds.map(({ icon: Icon, n }) => (
            <li
              key={n}
              className={
                offer
                  ? // Frosted glass with a white edge on the landing (Eugene,
                    // 07.10): the background photo shows through, softened.
                    "rounded-[20px] border border-white/80 bg-white/45 p-8 backdrop-blur-xl"
                  : "rounded-[20px] border border-white bg-white/90 p-8"
              }
            >
              {offer ? (
                // The card's icon with a small bright-green check badge on its
                // bottom-right corner (Eugene, 07.10).
                <span className="relative inline-flex">
                  <Icon
                    aria-hidden
                    size={48}
                    className="text-neutral-900"
                    style={{ maskImage: BADGE_CUT, WebkitMaskImage: BADGE_CUT }}
                  />
                  <span className="bg-bright-green-400 absolute top-[26px] left-[26px] flex size-6 items-center justify-center rounded-full text-neutral-900">
                    <svg aria-hidden viewBox="0 0 24 24" className="size-4">
                      <path
                        d="M6 12.5l4 4 8-9"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={3.2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
              ) : (
                <Icon aria-hidden size={48} className="text-neutral-900" />
              )}
              <p className="type-body-xl mt-8 font-medium text-pretty text-neutral-900/50">
                <span className="text-neutral-900">
                  {t(`${prefix}${n}Lead`)}
                </span>{" "}
                {t(`${prefix}${n}Rest`)}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

/**
 * What the yearly price buys, under the finds cards on the `/go/bloodtest`
 * landing (Eugene, 07.10): an outlined card on the page grey with a centred
 * heading, three verb-led points under small check circles, the plan's
 * booking button with its price, a "cancel any time" pill and a note.
 * Needs MriPlanScope for the price.
 */
export function BloodOfferSection({
  trackingLocation,
}: {
  trackingLocation: string
}) {
  const t = useTranslations("pricingV5")
  const { money, tier } = usePlanPrices()
  return (
    <section
      aria-labelledby="blood-offer-heading"
      className="bg-white px-4 py-16 md:px-10 md:py-24"
    >
      <div className="mx-auto flex max-w-[760px] flex-col items-center text-center">
        <h2
          id="blood-offer-heading"
          className="type-display-sm-emphasized text-balance text-neutral-900"
        >
          {t("bloodOfferHeading")}
        </h2>
        <ul className="mt-10 grid w-full max-w-[680px] grid-cols-1 gap-6 md:mt-12 md:grid-cols-3 md:gap-4">
          {([1, 2, 3] as const).map((n) => (
            <li key={n} className="flex flex-col items-center gap-3">
              <span className="bg-bright-green-200 flex size-11 items-center justify-center rounded-full text-neutral-900">
                <svg aria-hidden viewBox="0 0 24 24" className="size-6">
                  <path
                    d="M5.5 12.5l4.25 4.25L18.5 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <p className="type-body-lg max-w-[210px] text-balance text-neutral-900">
                {t(`bloodOfferPoint${n}`)}
              </p>
            </li>
          ))}
        </ul>
        <BookButton
          scan="blood"
          label={t("bloodOfferCta", { price: money(tier("once")) })}
          trackingLabel="go_bloodtest_offer_cta"
          trackingLocation={trackingLocation}
          size="lg"
          arrow={false}
          className="mt-10 block w-full sm:w-auto md:mt-12"
          buttonClassName="w-full md:px-10 max-sm:h-auto max-sm:min-h-12 max-sm:whitespace-normal max-sm:px-5 max-sm:py-3 max-sm:text-balance"
        />
        <p className="type-body-md mt-4 max-w-[520px] text-balance text-neutral-600">
          {t("bloodOfferNote")}
        </p>
      </div>
    </section>
  )
}

/** The homepage's Ahead-against-a-standard-check-up table, cut to the blood
 *  test for the `/go/bloodtest` landing, with the offer's button (Eugene,
 *  07.10). Needs MriPlanScope for the live price. */
export function BloodCompare({
  trackingLocation,
}: {
  trackingLocation: string
}) {
  const t = useTranslations("pricingV5")
  const { money, tier } = usePlanPrices()
  const price = money(tier("once"))
  return (
    <HomeV4Compare
      // The homepage's SECTION shell, written out so this page does not
      // pull in the whole homepage client.
      section="mx-auto max-w-[1440px] px-5 md:px-10"
      planPrice={price}
      blood
      cta={
        <BookButton
          scan="blood"
          label={t("bloodOfferCta", { price })}
          trackingLabel="go_bloodtest_compare_cta"
          trackingLocation={trackingLocation}
          size="lg"
          arrow={false}
          className="block w-full sm:w-auto"
          buttonClassName="w-full md:px-10 max-sm:h-auto max-sm:min-h-12 max-sm:whitespace-normal max-sm:px-5 max-sm:py-3 max-sm:text-balance"
        />
      }
    />
  )
}

/** The `/go/bloodtest` landing's closing block: the homepage's photo band
 *  and benefits, but it asks for the booking, not the quiz (Eugene, 07.10).
 *  Needs MriPlanScope for the price. */
export function BloodClose({ trackingLocation }: { trackingLocation: string }) {
  const t = useTranslations("pricingV5")
  const { money, tier } = usePlanPrices()
  return (
    <HomeV4Close
      section="mx-auto max-w-[1440px] px-5 md:px-10"
      lede={t("bloodCloseLede")}
      book={{
        heading: {
          plain: `${t("bloodCloseHeadingPlain")} `,
          accent: t("bloodCloseHeadingAccent"),
        },
        cta: (
          <BookButton
            scan="blood"
            label={t("bloodOfferCta", { price: money(tier("once")) })}
            trackingLabel="go_bloodtest_close_cta"
            trackingLocation={trackingLocation}
            size="lg"
            arrow={false}
            className="block w-full sm:w-auto"
            buttonClassName="w-full bg-white px-8 text-neutral-950 hover:bg-neutral-100 hover:shadow-none md:px-10 max-sm:h-auto max-sm:min-h-12 max-sm:whitespace-normal max-sm:px-5 max-sm:py-3 max-sm:text-balance"
          />
        ),
      }}
    />
  )
}

/** The membership-first add-on tests' pages: slug → their cards' copy and
 *  icons. Each ends on what the test cannot tell you. */
const ADDON_FINDS: Record<
  string,
  { prefix: FindsPrefix; icons: readonly PhosphorIcon[] }
> = {
  "coeliac-blood-test": {
    prefix: "coeliacFinds",
    icons: [MagnifyingGlass, Drop, Bread, Stethoscope],
  },
  "heavy-metals-blood-test": {
    prefix: "heavyMetalsFinds",
    icons: [Fish, House, MagnifyingGlass, Hourglass],
  },
  "trace-elements-blood-test": {
    prefix: "traceElementsFinds",
    icons: [ShieldCheck, ForkKnife, Pill, Stethoscope],
  },
}

/** `BloodFindsCards` for an add-on test's page, or nothing for other tests. */
export function AddonFindsCards({ slug }: { slug: string }) {
  const finds = ADDON_FINDS[slug]
  return finds ? <BloodFindsCards {...finds} /> : null
}

export function BloodDoctors({
  experts,
  afterGrey = false,
  whiteCard = false,
}: {
  experts: ReviewerExpert[]
  /** The landing sets the section on full-width white, and the doctor
   *  cards turn light grey on it (Eugene, 07.10). */
  whiteCard?: boolean
  /** Straight under another grey section, whose bottom padding already
   *  parts the two: a short top so the gap is not doubled. The landing
   *  layout puts it under the diseases (Eugene, 07.10). */
  afterGrey?: boolean
}) {
  const t = useTranslations("pricingV5")
  const team = useTranslations("about")
  const home = useTranslations("homeV4")
  const lp = useLocalePath()
  const anna = experts.find((expert) => expert.name.includes("Erat"))
  const battegay = experts.find((expert) => expert.name.includes("Battegay"))
  // The same GP profiles and curated portraits as About's wider team.
  // These doctors are not all flagged as featured reviewers in the CMS.
  const cards = [
    ...(battegay
      ? [{ ...battegay, title: home("doctorsCaptionBattegay") }]
      : []),
    {
      name: "Dr. Agata Jagoda",
      title: team("widerTeamGpPrevention"),
      image: blobMediaUrl("/images/about/team/agata.webp"),
    },
    {
      name: "Dr. Hadrien Komaromi",
      title: team("widerTeamGpPreventionM"),
      image: blobMediaUrl("/images/about/team/hadrien.webp"),
    },
  ]
  if (!anna) return null

  return (
    <section
      id="blood-doctors"
      aria-labelledby="blood-doctors-heading"
      className={cn(
        "pb-16 md:pb-[115px]",
        whiteCard ? "bg-white" : "bg-background",
        afterGrey && !whiteCard ? "pt-4 md:pt-6" : "pt-16 md:pt-[92px]"
      )}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <p className="type-label-lg text-neutral-500">
          {t("bloodDoctorsEyebrow")}
        </p>
        <h2
          id="blood-doctors-heading"
          className="type-display-md-emphasized mt-[11px] max-w-[640px] text-neutral-900"
        >
          {t("bloodDoctorsHeading")}
        </h2>
        <figure className="mt-10 flex flex-col gap-6 md:mt-[60px] md:flex-row md:gap-10">
          <Image
            src={blobMediaUrl("/images/doctors/erat.jpeg")}
            alt={anna.name}
            width={108}
            height={108}
            sizes="108px"
            className="size-[108px] shrink-0 rounded-[12px] object-cover"
          />
          <div className="max-w-[820px]">
            <p className="type-headline-md font-normal text-neutral-900">
              {t("bloodDoctorsStatement")}
            </p>
            <figcaption className="mt-4">
              <p className="type-body-lg-emphasized text-neutral-950">
                {anna.name}
              </p>
              <p className="type-body-md mt-1 text-neutral-500">
                {t("bloodDoctorsCaptionErat")}
              </p>
            </figcaption>
          </div>
        </figure>
        <ul className="mt-12 grid max-w-[1142px] grid-cols-1 gap-3 md:mt-[52px] md:ml-[136px] md:gap-[18px] lg:grid-cols-3">
          {cards.map((doctor) => (
            <li
              key={doctor.name}
              className={cn(
                "flex items-center gap-4 rounded-[20px] p-3 md:gap-[18px]",
                whiteCard ? "bg-neutral-50" : "bg-white"
              )}
            >
              <Image
                src={doctor.image}
                alt={doctor.name}
                width={144}
                height={144}
                sizes="72px"
                className="size-[72px] shrink-0 rounded-[12px] object-cover"
              />
              <div className="flex flex-col gap-1">
                <p className="type-body-lg-emphasized text-neutral-950">
                  {doctor.name}
                </p>
                <p className="type-body-md text-neutral-500">{doctor.title}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-8 md:ml-[148px]">
          <Link
            href={`${lp("/about")}#team`}
            className="type-body-md inline-flex items-center gap-1.5 font-medium text-neutral-900 underline-offset-4 hover:underline"
          >
            {t("mriDoctorsTeamLink")}
            <ArrowRight aria-hidden size={18} />
          </Link>
        </div>
        {whiteCard && (
          // The landing's partner and press logos, with the people they
          // vouch for (Eugene, 07.10).
          <PartnerLogoMarquee
            layout="static"
            only={["Siemens Healthineers", "Unilabs", "Google", "TUM"]}
            className="mt-14 flex-nowrap gap-x-5 pt-0 md:mt-20 md:gap-x-16 max-md:[&_img]:!h-[18px]"
          />
        )}
      </div>
    </section>
  )
}
